import React, { useEffect, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Form,
  DatePicker,
  Descriptions,
  Row,
  Col,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const LoanUserBanList = () => {
  const token = useSelector((state) => state.user.token);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
  });

  // =========================
  // SEARCH + FILTER
  // =========================

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const [sorter, setSorter] = useState({
    field: 'created_at',
    order: 'descend',
  });

  // =========================
  // MODAL
  // =========================

  const [formVisible, setFormVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);

  const [selectedBan, setSelectedBan] = useState(null);

  const [form] = Form.useForm();

  // =========================
  // USERS
  // =========================

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  // =========================
  // FETCH DATA
  // =========================

  const fetchData = async (page = 1, limit = 10) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      params.append('page', page);
      params.append('limit', limit);

      if (search) {
        params.append('search', search);
      }

      if (status) {
        params.append('status', status);
      }

      if (dateRange && dateRange.length === 2) {
        params.append(
          'start_date',
          dateRange[0].format('YYYY-MM-DD')
        );

        params.append(
          'end_date',
          dateRange[1].format('YYYY-MM-DD')
        );
      }

      if (sorter.field) {
        params.append('sort_by', sorter.field);
      }

      if (sorter.order) {
        params.append(
          'sort_order',
          sorter.order === 'ascend' ? 'asc' : 'desc'
        );
      }

      const res = await apiCall({
        method: 'GET',
        url: `/loan-user-bans?${params.toString()}`,
        data: {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
      });

      if (res.status === 'success') {
        setData(res.data || []);

        setMeta({
          page: res.pagination?.page || page,
          limit: res.pagination?.limit || limit,
          total: res.pagination?.total_data || 0,
        });
      } else {
        message.error(res.message);
      }
    } catch (error) {
      console.error(error);
      message.error('Gagal mengambil data banned user');
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // FETCH USERS
  // =========================

  const fetchUsers = async () => {
    setUserLoading(true);

    try {
      /*
       * GANTI endpoint ini dengan endpoint list user
       * yang memang sudah ada di project kamu.
       *
       * Yang dibutuhkan cuma:
       * users_id
       * name
       * users_code
       */
      const res = await apiCall({
        method: 'GET',
        url: '/users?limit=1000',
        data: {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
      });

      if (res.status === 'success') {
        // Sesuaikan kalau response list user kamu berbeda.
        const result = res.data?.data || res.data || [];

        setUsers(result);
      }
    } catch (error) {
      console.error(error);
      message.error('Gagal mengambil data user');
    } finally {
      setUserLoading(false);
    }
  };

  // =========================
  // USE EFFECT
  // =========================

  useEffect(() => {
    fetchData(meta.page, meta.limit);
  }, [
    search,
    status,
    dateRange,
    sorter,
    meta.page,
    meta.limit,
  ]);

  // =========================
  // TABLE CHANGE
  // =========================

  const handleTableChange = (
    pagination,
    filters,
    sorterObj
  ) => {
    setSorter({
      field: sorterObj.field || 'created_at',
      order: sorterObj.order || 'descend',
    });

    setMeta((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  // =========================
  // CREATE MODAL
  // =========================

  const openCreateModal = async () => {
    setSelectedBan(null);

    form.resetFields();

    await fetchUsers();

    setFormVisible(true);
  };

  // =========================
  // EDIT MODAL
  // =========================

  const openEditModal = (record) => {
    setSelectedBan(record);

    form.setFieldsValue({
      start_date: dayjs(record.start_date),
      end_date: dayjs(record.end_date),
      reason: record.reason,
    });

    setFormVisible(true);
  };

  // =========================
  // DETAIL
  // =========================

  const openDetailModal = async (record) => {
    setLoading(true);

    try {
      const res = await apiCall({
        method: 'GET',
        url: `/loan-user-bans/${record.loan_user_ban_id}`,
        data: {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
      });

      if (res.status === 'success') {
        setSelectedBan(res.data);
        setDetailVisible(true);
      } else {
        message.error(res.message);
      }
    } catch (error) {
      console.error(error);
      message.error('Gagal mengambil detail banned user');
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CREATE / UPDATE
  // =========================

  const submitForm = async () => {
    try {
      const values = await form.validateFields();

      // =========================
      // UPDATE
      // =========================

      if (selectedBan) {
        const payload = {
          start_date: values.start_date.format('YYYY-MM-DD'),
          end_date: values.end_date.format('YYYY-MM-DD'),
          reason: values.reason,
        };

        const res = await apiCall({
          method: 'PUT',
          url: `/loan-user-bans/${selectedBan.loan_user_ban_id}`,
          data: {
            data: payload,
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          },
        });

        if (res.status === 'success') {
          message.success('Banned user berhasil diperbarui');

          setFormVisible(false);
          setSelectedBan(null);
          form.resetFields();

          fetchData(meta.page, meta.limit);
        } else {
          message.error(res.message);
        }

        return;
      }

      // =========================
      // CREATE
      // =========================

      const payload = {
        users_ids: values.users_ids,
        start_date: values.start_date.format('YYYY-MM-DD'),
        end_date: values.end_date.format('YYYY-MM-DD'),
        reason: values.reason,
      };

      const res = await apiCall({
        method: 'POST',
        url: '/loan-user-bans',
        data: {
          data: payload,
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
        },
      });

      if (res.status === 'success') {
        const created = res.data?.created || [];
        const skipped = res.data?.skipped || [];

        if (created.length > 0) {
          message.success(
            `${created.length} user berhasil dibanned`
          );
        }

        if (skipped.length > 0) {
          Modal.warning({
            title: 'Beberapa user tidak dibanned',
            content: (
              <div>
                {skipped.map((item) => (
                  <div key={item.users_id}>
                    User ID {item.users_id}: {item.reason}
                  </div>
                ))}
              </div>
            ),
          });
        }

        setFormVisible(false);
        form.resetFields();

        fetchData(meta.page, meta.limit);
      } else {
        message.error(res.message);
      }
    } catch (error) {
      console.error(error);

      if (!error?.errorFields) {
        message.error('Gagal menyimpan banned user');
      }
    }
  };

  // =========================
  // DELETE
  // =========================

  const deleteBan = async (record) => {
    try {
      const res = await apiCall({
        method: 'DELETE',
        url: `/loan-user-bans/${record.loan_user_ban_id}`,
        data: {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
      });

      if (res.status === 'success') {
        message.success('Data banned berhasil dihapus');

        // Kalau data terakhir di page
        if (data.length === 1 && meta.page > 1) {
          setMeta((prev) => ({
            ...prev,
            page: prev.page - 1,
          }));
        } else {
          fetchData(meta.page, meta.limit);
        }
      } else {
        message.error(res.message);
      }
    } catch (error) {
      console.error(error);
      message.error('Gagal menghapus banned user');
    }
  };

  // =========================
  // STATUS
  // =========================

  const renderStatus = (statusValue) => {
    switch (statusValue) {
      case 'active':
        return <Tag color="red">Active</Tag>;

      case 'upcoming':
        return <Tag color="blue">Upcoming</Tag>;

      case 'expired':
        return <Tag>Expired</Tag>;

      case 'revoked':
        return <Tag color="orange">Revoked</Tag>;

      case 'inactive':
        return <Tag color="default">Inactive</Tag>;

      default:
        return <Tag>{statusValue}</Tag>;
    }
  };

  // =========================
  // TABLE COLUMNS
  // =========================

  const columns = [
    {
      title: 'No',
      width: 70,
      render: (_, __, index) =>
        (meta.page - 1) * meta.limit + index + 1,
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      sorter: false,
      render: (_, record) => (
        <div>
          <div>
            <b>{record.user_name}</b>
          </div>

          <small>
            {record.users_code || `ID: ${record.users_id}`}
          </small>
        </div>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      sorter: true,
      render: (value) =>
        value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      sorter: true,
      render: (value) =>
        value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value}>
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Banned By',
      dataIndex: 'banned_by_name',
      render: (_, record) => (
        <div>
          <div>{record.banned_by_name || '-'}</div>

          {record.banned_by_users_code && (
            <small>{record.banned_by_users_code}</small>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: renderStatus,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      sorter: true,
      render: (value) =>
        value
          ? dayjs(value).format('YYYY-MM-DD HH:mm')
          : '-',
    },
    {
      title: 'Action',
      width: 210,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Detail">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openDetailModal(record)}
            />
          </Tooltip>

          {record.status !== 'revoked' && (
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
          )}


          <Popconfirm
            title="Hapus data banned?"
            description="Data banned akan dihapus."
            okText="Hapus"
            cancelText="Batal"
            onConfirm={() => deleteBan(record)}
          >
            <Tooltip title="Delete">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // =========================
  // RESET FILTER
  // =========================

  const resetFilter = () => {
    setSearch('');
    setStatus('');
    setDateRange(null);

    setSorter({
      field: 'created_at',
      order: 'descend',
    });

    setMeta((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  return (
    <div>
      <h2>Loan User Ban</h2>

      {/* ================= FILTER ================= */}

      <Row gutter={[10, 10]} style={{ marginBottom: 15 }}>
        <Col xs={24} sm={24} md={7}>
          <Input
            placeholder="Search user, code, reason..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);

              setMeta((prev) => ({
                ...prev,
                page: 1,
              }));
            }}
            allowClear
          />
        </Col>

        <Col xs={24} sm={8} md={4}>
          <Select
            placeholder="Status"
            value={status || undefined}
            style={{ width: '100%' }}
            allowClear
            onChange={(value) => {
              setStatus(value || '');

              setMeta((prev) => ({
                ...prev,
                page: 1,
              }));
            }}
          >
            <Option value="active">Active</Option>
            <Option value="upcoming">Upcoming</Option>
            <Option value="expired">Expired</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </Col>

        <Col xs={24} sm={16} md={7}>
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            format="YYYY-MM-DD"
            onChange={(dates) => {
              setDateRange(dates);

              setMeta((prev) => ({
                ...prev,
                page: 1,
              }));
            }}
          />
        </Col>

        <Col xs={24} sm={24} md={6}>
          <Space>
            <Button onClick={resetFilter}>
              Reset
            </Button>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              Banned User
            </Button>
          </Space>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}

      <Table
        columns={columns}
        dataSource={data}
        rowKey="loan_user_ban_id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} dari ${total} data`,
        }}
        onChange={handleTableChange}
      />

      {/* ================= CREATE / UPDATE ================= */}

      <Modal
        title={
          selectedBan
            ? 'Edit Banned User'
            : 'Banned User'
        }
        open={formVisible}
        onCancel={() => {
          setFormVisible(false);
          setSelectedBan(null);
          form.resetFields();
        }}
        onOk={submitForm}
        okText={selectedBan ? 'Update' : 'Banned'}
        width={600}
      >
        <Form
          layout="vertical"
          form={form}
        >
          {!selectedBan && (
            <Form.Item
              name="users_ids"
              label="Users"
              rules={[
                {
                  required: true,
                  message: 'Pilih minimal satu user',
                },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                loading={userLoading}
                placeholder="Pilih user yang akan dibanned"
                optionFilterProp="label"
                options={users.map((item) => ({
                  value: item.users_id,
                  label: `${item.name} ${
                    item.users_code
                      ? `(${item.users_code})`
                      : ''
                  }`,
                }))}
              />
            </Form.Item>
          )}

          {selectedBan && (
            <Descriptions
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 20 }}
            >
              <Descriptions.Item label="User">
                {selectedBan.user_name}
              </Descriptions.Item>

              <Descriptions.Item label="User Code">
                {selectedBan.users_code || '-'}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Row gutter={15}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="Start Date"
                rules={[
                  {
                    required: true,
                    message: 'Start date wajib diisi',
                  },
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
<Form.Item
    name="end_date"
    label="End Date"
    dependencies={['start_date']}
    rules={[
        {
            required: true,
            message: 'End date wajib diisi',
        },
        ({ getFieldValue }) => ({
            validator(_, value) {
                const startDate = getFieldValue('start_date');

                if (!value || !startDate) {
                    return Promise.resolve();
                }

                if (value.isBefore(startDate, 'day')) {
                    return Promise.reject(
                        new Error('End date tidak boleh sebelum start date')
                    );
                }

                return Promise.resolve();
            },
        }),
    ]}
>
    <DatePicker
        style={{ width: '100%' }}
        format="YYYY-MM-DD"
    />
</Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[
              {
                required: true,
                message: 'Reason wajib diisi',
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Masukkan alasan banned user"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ================= DETAIL ================= */}

      <Modal
        title="Detail Banned User"
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          setSelectedBan(null);
        }}
        footer={null}
        width={700}
      >
        {selectedBan && (
          <Descriptions
            bordered
            column={1}
          >
            <Descriptions.Item label="Ban ID">
              {selectedBan.loan_user_ban_id}
            </Descriptions.Item>

            <Descriptions.Item label="User">
              {selectedBan.user_name || '-'}
            </Descriptions.Item>

            <Descriptions.Item label="User Code">
              {selectedBan.users_code || '-'}
            </Descriptions.Item>

            <Descriptions.Item label="User ID">
              {selectedBan.users_id}
            </Descriptions.Item>

            <Descriptions.Item label="Start Date">
              {selectedBan.start_date
                ? dayjs(
                    selectedBan.start_date
                  ).format('YYYY-MM-DD')
                : '-'}
            </Descriptions.Item>

            <Descriptions.Item label="End Date">
              {selectedBan.end_date
                ? dayjs(
                    selectedBan.end_date
                  ).format('YYYY-MM-DD')
                : '-'}
            </Descriptions.Item>

            <Descriptions.Item label="Reason">
              {selectedBan.reason || '-'}
            </Descriptions.Item>

            <Descriptions.Item label="Status">
              {renderStatus(selectedBan.status)}
            </Descriptions.Item>

            <Descriptions.Item label="Banned By">
              {selectedBan.banned_by_name || '-'}
              {selectedBan.banned_by_users_code
                ? ` (${selectedBan.banned_by_users_code})`
                : ''}
            </Descriptions.Item>

            <Descriptions.Item label="Created At">
              {selectedBan.created_at
                ? dayjs(
                    selectedBan.created_at
                  ).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>

            <Descriptions.Item label="Updated At">
              {selectedBan.updated_at
                ? dayjs(
                    selectedBan.updated_at
                  ).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>

            {selectedBan.revoked_at && (
              <>
                <Descriptions.Item label="Revoked At">
                  {dayjs(
                    selectedBan.revoked_at
                  ).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>

                <Descriptions.Item label="Revoked By">
                  {selectedBan.revoked_by_name || '-'}
                  {selectedBan.revoked_by_users_code
                    ? ` (${selectedBan.revoked_by_users_code})`
                    : ''}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default LoanUserBanList;